import { useQuery } from '@tanstack/react-query';

import { listTriggerProfileSignals } from '@/lib/api/generated/api';
import type { SignalView } from '@/lib/api/generated/model';

import { SignalsUnavailableError } from '../errors';
import { triggerKeys } from '../queryKeys';

/**
 * What arrived against one trigger profile (spec 090).
 *
 * Distinct from `useTriggerProfileRuns` on purpose: a run is what *fired*, a
 * signal is what *arrived*, and the gap between them is the entire subject of
 * this surface. A signal that produced no run is invisible in the run history and
 * is exactly the case a reader opens the profile to find.
 *
 * **The page envelope stops here.** The service returns `items`/`total`/`limit`/
 * `offset`; callers get a list and a `hasMore` flag. Nothing above this hook
 * learns the listing is paged, which is what keeps the section a deep module
 * (constitution Principle 1) — and it means moving to cursors later changes this
 * file and no other.
 *
 * `hasMore` is derived here rather than in a component for the same reason: it is
 * the single test behind FR-014's notice, and a component recomputing
 * `total > signals.length` would be a second place for that rule to drift.
 *
 * Anything but a 200 throws `SignalsUnavailableError` rather than resolving to an
 * empty list. Collapsing a failed read into "nothing arrived" would be a confident,
 * wrong answer on the one surface whose purpose is telling a reader whether
 * anything did (FR-009).
 *
 * No `refetchInterval`, by FR-022. A signal stuck mid-processing stays on screen
 * as it was until the reader asks again; this is a record of what happened, not a
 * monitor.
 */
export function useTriggerProfileSignals(
  workspaceId: string,
  triggerProfileId: string,
) {
  const query = useQuery({
    queryKey: triggerKeys.signals(workspaceId, triggerProfileId),
    queryFn: async (): Promise<{ items: SignalView[]; total: number }> => {
      const response = await listTriggerProfileSignals(
        workspaceId,
        triggerProfileId,
      );
      if (response.status !== 200) {
        throw new SignalsUnavailableError(response.status);
      }
      // The default bound is the service's, not restated here. A client that
      // names the number owns it forever, and the service is where the cost of
      // the page is actually paid.
      return { items: response.data.items, total: response.data.total };
    },
    retry: false,
  });

  const signals = query.data?.items;
  const total = query.data?.total ?? 0;

  return {
    signals,
    total,
    /** Whether the profile's history runs past the page this read returned. */
    hasMore: signals !== undefined && total > signals.length,
    isPending: query.isPending,
    loadFailed: query.isError,
    refetch: query.refetch,
    /** Read again after a failure (spec 126 FR-009). */
    retry: () => {
      void query.refetch();
    },
    isFetching: query.isFetching,
  };
}
